/**
 * React bindings for the EMIL flagservice OpenFeature flags, published under the
 * `@emilgroup/flags/react` subpath (re-exported from `./react`).
 *
 * {@link FlagsProvider} owns an {@link EmilFlagsProvider} instance and exposes
 * its resolved values through context + hooks, so consuming apps read flags
 * with `useBooleanFlag` / `useStringFlag` / … and never import or wire up
 * OpenFeature themselves — OpenFeature stays an implementation detail of this
 * package. It mirrors the capability provider's design (same client options, a
 * parameterized `token` getter, fail-open behaviour).
 */
import * as React from "react";
import {
  ProviderEvents,
  type EvaluationContext,
  type JsonValue,
  type Logger,
} from "@openfeature/web-sdk";

import { localStorageToken } from "./local-storage-token";
import { EmilFlagsProvider, type FlagSnapshot } from "./provider";
import type { EmilFlagsProviderOptions } from "./types";
import type { WireEvaluationContext } from "./wire";

// Default token source when an app doesn't pass `token`: read the active
// account's bearer token from localStorage (`APP_TOKEN.accessToken`). Apps that
// keep their token elsewhere pass their own `token`; pass `() => undefined` to
// send no token at all.
const defaultToken = localStorageToken();

// The provider's resolve* methods take a context (ignored — values are served
// from the cache populated at initialize/onContextChange) and a logger (used
// only on a value parse error). Supply inert ones: parse errors surface via the
// resolution `reason`, and resolve runs on every render, so logging would spam.
const EMPTY_CONTEXT: EvaluationContext = {};
const NOOP_LOGGER: Logger = {
  error: () => {},
  warn: () => {},
  info: () => {},
  debug: () => {},
};

interface FlagsContextValue {
  /** The flag provider, or `undefined` if the options were invalid. */
  provider: EmilFlagsProvider | undefined;
  /** True once the first evaluation has settled (success or failure). */
  ready: boolean;
  /** Bumped on ready / configuration-change so flag hooks re-read. */
  revision: number;
}

const FlagsContext = React.createContext<FlagsContextValue | undefined>(undefined);

export interface FlagsProviderProps extends EmilFlagsProviderOptions {
  /**
   * Optional evaluation context for per-user targeting / percentage rollouts.
   * The tenant is always derived server-side from the bearer token / SDK key.
   */
  context?: WireEvaluationContext;
  /**
   * Called when the initial evaluation fails. The provider still **fails open**
   * (serves caller-supplied defaults) so a flagservice outage never blocks the
   * app.
   */
  onError?: (error: unknown) => void;
  children?: React.ReactNode;
}

/**
 * Resolves flags for the current context and provides them to the React tree.
 * Mount it where a bearer token (or a build-time `clientSdkKey`) is available;
 * it evaluates on mount, refreshes in the background (see `pollIntervalMs`), and
 * re-renders flag consumers when a value changes. If the token only becomes
 * available later, key a parent so this remounts and re-initializes.
 */
export function FlagsProvider(props: FlagsProviderProps): React.ReactElement {
  const {
    children,
    context,
    onError,
    baseUrl,
    clientSdkKey,
    environmentKey,
    token,
    fetch: fetchImpl,
    timeoutMs,
    pollIntervalMs,
  } = props;

  const [ready, setReady] = React.useState(false);
  const [revision, setRevision] = React.useState(0);

  // `token`/`onError` may have a fresh identity each render; read them through
  // refs so they don't retrigger the effect while always using the latest value.
  const tokenRef = React.useRef(token);
  tokenRef.current = token;
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;

  // Build the provider from the primitive options. `token` is read via the ref
  // so a fresh getter identity each render doesn't rebuild the provider /
  // refetch the ruleset.
  const provider = React.useMemo<EmilFlagsProvider | undefined>(() => {
    try {
      return new EmilFlagsProvider({
        baseUrl,
        clientSdkKey,
        environmentKey,
        timeoutMs,
        pollIntervalMs,
        fetch: fetchImpl,
        token: () => {
          const current = tokenRef.current;
          if (current == null) return defaultToken();
          return typeof current === "function" ? current() : current;
        },
      });
    } catch {
      return undefined;
    }
  }, [baseUrl, clientSdkKey, environmentKey, timeoutMs, pollIntervalMs, fetchImpl]);

  const contextKey = context?.key;
  const contextAttributes = context?.attributes;

  React.useEffect(() => {
    let cancelled = false;

    if (!provider) {
      // Misconfiguration (e.g. neither clientSdkKey nor environmentKey set).
      onErrorRef.current?.(
        new Error("@emilgroup/flags: clientSdkKey or environmentKey is required"),
      );
      setReady(true);
      return;
    }

    const bump = (): void => {
      if (!cancelled) {
        setRevision((value) => value + 1);
      }
    };
    provider.events.addHandler(ProviderEvents.ConfigurationChanged, bump);

    provider
      .initialize(toEvaluationContext(contextKey, contextAttributes))
      .then(() => {
        if (!cancelled) {
          setReady(true);
          bump();
        }
      })
      .catch((error) => {
        // Fail open: reveal caller-supplied defaults even if init failed (e.g.
        // the service is unreachable or the token isn't authorized).
        if (!cancelled) {
          onErrorRef.current?.(error);
          setReady(true);
        }
      });

    return () => {
      cancelled = true;
      provider.events.removeHandler(ProviderEvents.ConfigurationChanged, bump);
      void provider.onClose();
    };
  }, [provider, contextKey, contextAttributes]);

  const value = React.useMemo<FlagsContextValue>(
    () => ({ provider, ready, revision }),
    [provider, ready, revision],
  );

  return <FlagsContext.Provider value={value}>{children}</FlagsContext.Provider>;
}

/**
 * Build an OpenFeature evaluation context from the wire shape accepted by
 * {@link FlagsProviderProps.context}. `attributes` is a JSON-encoded object (the
 * shape the flagservice expects); it is decoded and spread alongside the
 * targeting key. Malformed JSON is ignored (treated as no attributes).
 */
function toEvaluationContext(
  key: string | undefined,
  attributes: string | undefined,
): EvaluationContext {
  let parsed: Record<string, unknown> = {};
  if (attributes) {
    try {
      const decoded = JSON.parse(attributes);
      if (decoded && typeof decoded === "object") {
        parsed = decoded as Record<string, unknown>;
      }
    } catch {
      // Ignore malformed attributes; evaluate with the targeting key alone.
    }
  }
  // JSON-decoded attributes are dynamic; the provider re-serializes them via
  // toWireContext, so cast to the OpenFeature shape here.
  const ctx: Record<string, unknown> = key
    ? { targetingKey: key, ...parsed }
    : { ...parsed };
  return ctx as EvaluationContext;
}

function useFlagsContext(): FlagsContextValue {
  const ctx = React.useContext(FlagsContext);
  if (!ctx) {
    throw new Error(
      "useBooleanFlag / useStringFlag / … must be used within <FlagsProvider>.",
    );
  }
  return ctx;
}

/** Whether the flags have finished their first resolution (success or failure). */
export function useFlagsReady(): boolean {
  return useFlagsContext().ready;
}

/** Resolve a boolean flag for the current context. */
export function useBooleanFlag(flagKey: string, defaultValue: boolean): boolean {
  const { provider } = useFlagsContext();
  return provider
    ? provider.resolveBooleanEvaluation(flagKey, defaultValue, EMPTY_CONTEXT, NOOP_LOGGER)
        .value
    : defaultValue;
}

/** Resolve a string flag for the current context. */
export function useStringFlag(flagKey: string, defaultValue: string): string {
  const { provider } = useFlagsContext();
  return provider
    ? provider.resolveStringEvaluation(flagKey, defaultValue, EMPTY_CONTEXT, NOOP_LOGGER)
        .value
    : defaultValue;
}

/** Resolve a number flag for the current context. */
export function useNumberFlag(flagKey: string, defaultValue: number): number {
  const { provider } = useFlagsContext();
  return provider
    ? provider.resolveNumberEvaluation(flagKey, defaultValue, EMPTY_CONTEXT, NOOP_LOGGER)
        .value
    : defaultValue;
}

/** Resolve an object (JSON) flag for the current context. */
export function useObjectFlag<T>(flagKey: string, defaultValue: T): T {
  const { provider } = useFlagsContext();
  if (!provider) {
    return defaultValue;
  }
  return provider.resolveObjectEvaluation(
    flagKey,
    defaultValue as unknown as JsonValue,
    EMPTY_CONTEXT,
    NOOP_LOGGER,
  ).value as T;
}

/**
 * A snapshot of every resolved flag (`key`, `value`, `variant`, `reason`),
 * ordered by key, re-read on every render so it tracks the latest values.
 * Useful for debug panels — OpenFeature clients can't otherwise enumerate
 * flags. Empty until the provider is ready.
 */
export function useAllFlags(): FlagSnapshot[] {
  const { provider } = useFlagsContext();
  return provider ? provider.listFlags() : [];
}

export interface FeatureProps {
  /** Boolean flag key to gate on. */
  flag: string;
  /** Rendered when the flag resolves to false (defaults to rendering nothing). */
  fallback?: React.ReactNode;
  /** Value used until the flag resolves, or if it is missing. Defaults to `false`. */
  defaultValue?: boolean;
  children?: React.ReactNode;
}

/**
 * Renders `children` only when the boolean `flag` is on, otherwise `fallback`.
 *
 * @example
 * <Feature flag="new-checkout"><NewCheckout /></Feature>
 */
export function Feature(props: FeatureProps): React.ReactElement {
  const { flag, fallback = null, defaultValue = false, children } = props;
  const enabled = useBooleanFlag(flag, defaultValue);
  return <React.Fragment>{enabled ? children : fallback}</React.Fragment>;
}

export type { FlagSnapshot } from "./provider";
